/**
 * @file MeetingExportSheet.tsx
 * @brief B50 (DES 02-11-2026): Export / Send only the parts you want, on the
 *        meeting screen (web + Mac app).
 *
 * @details
 * Presets on top (Full minutes, Summary, To-dos, Decisions, My to-dos), then
 * the sections on a timeline rail (U2, the phone's Export / Send sheet): each
 * node is the section's tick (our picker's circle / circle-check, a half node
 * when part-picked, "3/7"); clicking the row opens its items under it as a
 * thinner nested rail. Transcript is off by default ("Off") and takes a
 * from / to range once ticked. An optional short message goes at the top. On the right a
 * live preview, built by the server from the sender's own view of the meeting
 * (so nothing goes out that they cannot see).
 *
 * Out: "Download PDF" (Mac app with the B50-D bridge: "Save PDF" + "Share…"),
 * and "Copy text". Our server sends no email. The server remembers the last
 * pick; the sheet opens on it.
 *
 * The quick path from a to-do or decision row is `useShareOneItem`
 * (../exportPickApi).
 *
 * @references docs/product/meeting-export-b50.md, ./meetingExportPick.ts
 * @date 02-11-2026 (B50-W)
 */

import { useEffect, useMemo, useState } from "react";
import {
  ChevronRight,
  Circle,
  CircleCheck,
  CircleMinus,
  Copy,
  FileText,
  LoaderCircle,
  Share2,
  ArrowDownToLine,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { isRTLText } from "@/shared/utils/formatters";
import {
  canShareFileNatively,
  copyTextFrom,
  savePdf,
  sharePdfNatively,
  useMeetingExportCatalogue,
  useMeetingExportPdf,
  useMeetingExportPreview,
  useMeetingExportText,
  type ExportPreviewSection,
} from "../exportPickApi";
import {
  TRANSCRIPT_KEY,
  applyPreset,
  clock,
  initialPick,
  matchingPreset,
  parseClock,
  pickBody,
  sectionCount,
  sectionTick,
  setTranscriptRange,
  toggleItem,
  toggleSection,
  type ExportCatalogue,
  type ExportItem,
  type ExportSection,
  type PickState,
  type SectionTick,
} from "./meetingExportPick";

function errorText(err: unknown): string {
  return err instanceof ApiClientError || err instanceof Error ? err.message : "Please try again.";
}

/** The preset chips' fill, also on the grey footer pills (U2: "Copy text"). */
const CHIP_FILL =
  "bg-black/[0.05] text-foreground hover:bg-black/[0.09] dark:bg-white/[0.07] dark:hover:bg-white/[0.12]";

/**
 * Our picker tick (PickerSheet's Circle / CircleCheck in brand), on a page
 * coloured disc so the rail stops short of it; "partly" = a half node.
 */
function TickNode({ state, small }: { state: SectionTick; small?: boolean }) {
  const size = small ? "h-5 w-5" : "h-[22px] w-[22px]";
  const Icon = state === "on" ? CircleCheck : state === "partly" ? CircleMinus : Circle;
  return (
    <span aria-hidden className={cn("flex shrink-0 rounded-full bg-background", size)}>
      <Icon className={cn(size, state === "off" ? "text-muted-foreground/50" : "text-brand")} strokeWidth={2} />
    </span>
  );
}

/** A timeline rail segment: from the first node's middle to the last one's. */
function Rail({ first, last, mid, thin }: { first: boolean; last: boolean; mid: string; thin?: boolean }) {
  if (first && last) return null;
  return (
    <span
      aria-hidden
      style={first ? { top: mid } : last ? { height: mid } : undefined}
      className={cn(
        "absolute rounded-full bg-app-text/15",
        thin ? "left-[9.25px] w-[1.5px]" : "left-[10px] w-0.5",
        first ? "bottom-0" : last ? "top-0" : "inset-y-0",
      )}
    />
  );
}

/** One line for an item in the preview. */
function itemLine(key: string, item: ExportItem): { text: string; meta: string | null } {
  if (key === "summary") return { text: item.text ?? "", meta: item.label ?? null };
  if (key === "todos") {
    const meta = [item.owner, item.due ? `due ${item.due}` : null, item.done ? "done" : null]
      .filter(Boolean)
      .join(" · ");
    return { text: item.text ?? "", meta: meta || null };
  }
  if (key === "attendees") return { text: item.text ?? "", meta: item.guest ? "Guest" : null };
  if (key === "agendaCheck") return { text: item.text ?? "", meta: item.state ?? null };
  if (key === "keyPoints") return { text: item.text ?? "", meta: item.topic ?? null };
  return { text: item.text ?? "", meta: null };
}

/** An item's lines in the pick list: to-dos read "Owner · 12 Nov 2026" / "Owner · No due date" (U2). */
function pickLine(key: string, item: ExportItem): { text: string; meta: string | null } {
  if (key !== "todos") return itemLine(key, item);
  const meta = [item.owner, item.due || "No due date", item.done ? "Done" : null].filter(Boolean).join(" · ");
  return { text: item.text ?? "", meta };
}

function TranscriptRange({
  cat,
  pick,
  onChange,
}: {
  cat: ExportCatalogue;
  pick: PickState;
  onChange: (p: PickState) => void;
}) {
  const [from, setFrom] = useState(clock(pick.transcript.fromSecs));
  const [to, setTo] = useState(clock(pick.transcript.toSecs));
  useEffect(() => setFrom(clock(pick.transcript.fromSecs)), [pick.transcript.fromSecs]);
  useEffect(() => setTo(clock(pick.transcript.toSecs)), [pick.transcript.toSecs]);
  const commit = () => {
    const f = parseClock(from);
    const t = parseClock(to);
    onChange(
      setTranscriptRange(cat, pick, f ?? pick.transcript.fromSecs, t ?? pick.transcript.toSecs),
    );
  };
  const field =
    "h-9 w-[84px] rounded-lg border border-border bg-background px-2 text-center font-mono text-sm tabular-nums text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  return (
    <div className="-mt-1 flex flex-wrap items-center gap-2 pb-3 text-[13px] text-muted-foreground">
      <span>From</span>
      <input
        aria-label="Transcript from"
        value={from}
        onChange={(e) => setFrom(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && commit()}
        className={field}
      />
      <span>to</span>
      <input
        aria-label="Transcript to"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && commit()}
        className={field}
      />
      <button
        type="button"
        onClick={() => onChange(setTranscriptRange(cat, pick, 0, Number.MAX_SAFE_INTEGER))}
        className="rounded-full px-2 py-1 text-[13px] font-medium text-brand hover:bg-brand/10"
      >
        Whole meeting
      </button>
    </div>
  );
}

function SectionRow({
  cat,
  section,
  pick,
  onChange,
  first,
  last,
}: {
  cat: ExportCatalogue;
  section: ExportSection;
  pick: PickState;
  onChange: (p: PickState) => void;
  first: boolean;
  last: boolean;
}) {
  const isTranscript = section.key === TRANSCRIPT_KEY;
  const tick = sectionTick(cat, pick, section.key);
  const count = sectionCount(cat, pick, section.key);
  const expandable = !isTranscript && section.items.length > 0;
  const [open, setOpen] = useState(false);
  const ticked = new Set(pick.items[section.key] ?? []);
  const tickAll = () => onChange(toggleSection(cat, pick, section.key));

  return (
    <div className="relative pl-[34px]">
      {/* Header row is 44px tall: the node's middle sits at 22px. */}
      <Rail first={first} last={last} mid="22px" />
      {/* The node is the section's tick: click = tick / untick all. */}
      <button
        type="button"
        role="checkbox"
        aria-checked={tick === "on" ? true : tick === "partly" ? "mixed" : false}
        aria-label={section.label}
        onClick={tickAll}
        className="absolute -left-[6px] top-[5px] rounded-full p-[6px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <TickNode state={tick} />
      </button>
      {/* The row opens its items; without items (Transcript) it ticks, as the node. */}
      <button
        type="button"
        onClick={expandable ? () => setOpen((o) => !o) : tickAll}
        aria-expanded={expandable ? open : undefined}
        aria-label={expandable ? `${open ? "Hide" : "Show"} ${section.label} items` : undefined}
        className="flex h-11 w-full min-w-0 items-center gap-2 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-foreground">{section.label}</span>
        <span className="shrink-0 text-[13px] tabular-nums text-muted-foreground">
          {isTranscript ? (count ?? "Off") : count}
        </span>
        {expandable ? (
          <ChevronRight
            aria-hidden
            className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-90")}
          />
        ) : null}
      </button>
      {isTranscript && pick.transcript.on ? <TranscriptRange cat={cat} pick={pick} onChange={onChange} /> : null}
      {expandable && open ? (
        // Nested timeline: a thinner rail, smaller nodes, no card, no dividers.
        <div className="-mt-1 pb-3 pl-[2px]">
          {section.items.map((item, i) => {
            const { text, meta } = pickLine(section.key, item);
            const rtl = isRTLText(text);
            const on = ticked.has(item.id);
            return (
              <button
                key={item.id}
                type="button"
                role="checkbox"
                aria-checked={on}
                onClick={() => onChange(toggleItem(pick, section.key, item.id))}
                className="relative flex w-full items-start gap-3 rounded-lg py-1.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {/* 6px top padding + half a 20px node. */}
                <Rail first={i === 0} last={i === section.items.length - 1} mid="16px" thin />
                <span className="relative">
                  <TickNode state={on ? "on" : "off"} small />
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    dir={rtl ? "rtl" : "ltr"}
                    className="line-clamp-3 block text-sm leading-snug text-foreground/90"
                  >
                    {text || "—"}
                  </span>
                  {meta ? <span className="mt-0.5 block text-xs text-muted-foreground">{meta}</span> : null}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function PreviewItem({ section, item }: { section: ExportPreviewSection; item: ExportPreviewSection["items"][number] }) {
  if (section.key === TRANSCRIPT_KEY) {
    return (
      <li className="text-[13px] leading-relaxed text-foreground/85" dir={isRTLText(item.text ?? "") ? "rtl" : "ltr"}>
        <span className="font-mono text-xs text-muted-foreground">[{item.at}]</span>{" "}
        <span className="font-semibold">{item.speaker}:</span> {item.text}
      </li>
    );
  }
  if (section.key === "notes") {
    return (
      <li className="text-[13px] leading-relaxed text-foreground/85">
        <p className="font-semibold">{item.text}</p>
        <ul className="ml-4 list-disc">
          {(item.bullets ?? []).map((b, i) => (
            <li key={i}>
              {b.text}
              {b.children?.length ? (
                <ul className="ml-4 list-[circle]">
                  {b.children.map((c, j) => (
                    <li key={j}>{c}</li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      </li>
    );
  }
  const { text, meta } = itemLine(section.key, item);
  const rtl = isRTLText(text);
  return (
    <li className="text-[13px] leading-relaxed text-foreground/85" dir={rtl ? "rtl" : "ltr"}>
      {section.key === "summary" && item.label && section.items.length > 1 ? (
        <span className="font-semibold">{item.label}: </span>
      ) : null}
      {text}
      {section.key !== "summary" && meta ? <span className="text-muted-foreground"> ({meta})</span> : null}
    </li>
  );
}

function Preview({
  data,
  loading,
  empty,
}: {
  data: ReturnType<typeof useMeetingExportPreview>["data"];
  loading: boolean;
  empty: boolean;
}) {
  if (empty) {
    return <p className="p-5 text-sm text-muted-foreground">Pick at least one part to share.</p>;
  }
  if (!data) {
    return (
      <div className="flex items-center gap-2 p-5 text-sm text-muted-foreground">
        <LoaderCircle className="h-4 w-4 animate-spin" />
        Building the preview…
      </div>
    );
  }
  const p = data.preview;
  return (
    <div className={cn("space-y-4 p-5 transition-opacity", loading && "opacity-60")}>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-brand">{p.what}</p>
        <p className="mt-1 text-lg font-bold leading-tight text-foreground" dir={isRTLText(p.title) ? "rtl" : "ltr"}>
          {p.title}
        </p>
        <p className="text-[13px] text-muted-foreground">{p.date}</p>
      </div>
      {p.message ? (
        <div className="rounded-xl bg-black/[0.03] px-3 py-2 text-[13px] text-foreground/90 dark:bg-white/[0.05]">
          <span className="font-semibold">{p.message.from}:</span> {p.message.text}
        </div>
      ) : null}
      {p.sections.map((s) => (
        <div key={s.key}>
          <p className="mb-1 text-[13px] font-bold text-foreground">
            {s.label}
            {s.range ? (
              <span className="ml-1 font-normal text-muted-foreground">
                ({s.range.from}–{s.range.to})
              </span>
            ) : null}
          </p>
          <ul className={cn("space-y-1", s.key !== TRANSCRIPT_KEY && s.key !== "notes" && "ml-4 list-disc")}>
            {s.items.map((item, i) => (
              <PreviewItem key={`${s.key}:${item.id ?? i}`} section={s} item={item} />
            ))}
          </ul>
        </div>
      ))}
      <p className="border-t border-border/60 pt-3 text-xs text-muted-foreground">Made with VERBLIZR · verblizr.com</p>
    </div>
  );
}

export function MeetingExportSheet({
  open,
  onOpenChange,
  meetingId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meetingId: string;
}) {
  const catalogue = useMeetingExportCatalogue(meetingId, open);
  const cat = catalogue.data ?? null;
  const [pick, setPick] = useState<PickState | null>(null);
  const [message, setMessage] = useState("");
  // Fresh ticks every time the sheet opens, from THIS open's fetch (the
  // cached catalogue of the last open carries an older last pick).
  const [openedAt, setOpenedAt] = useState(0);
  useEffect(() => {
    if (open) {
      setOpenedAt(Date.now());
    } else {
      setPick(null);
      setMessage("");
    }
  }, [open]);
  useEffect(() => {
    if (open && cat && !pick && openedAt && catalogue.dataUpdatedAt >= openedAt) setPick(initialPick(cat));
  }, [open, cat, pick, openedAt, catalogue.dataUpdatedAt]);

  const body = useMemo(() => (cat && pick ? pickBody(cat, pick) : null), [cat, pick]);
  const activePreset = cat && pick ? matchingPreset(cat, pick) : null;

  // The preview follows the ticks ~300 ms after the last change.
  const [debounced, setDebounced] = useState<{ pick: NonNullable<typeof body>; message: string } | null>(null);
  useEffect(() => {
    if (!body) {
      setDebounced(null);
      return;
    }
    const t = window.setTimeout(() => setDebounced({ pick: body, message: message.trim() }), 300);
    return () => window.clearTimeout(t);
  }, [body, message]);
  const preview = useMeetingExportPreview(meetingId, open && debounced ? debounced : null);

  const exportText = useMeetingExportText();
  const exportPdf = useMeetingExportPdf();
  const nativeShare = canShareFileNatively();
  const busy = exportText.isPending || exportPdf.isPending;

  const onCopy = async () => {
    if (!body) return;
    try {
      await copyTextFrom(
        exportText.mutateAsync({ id: meetingId, pick: body, message }).then((r) => r.text),
      );
      toast({ variant: "success", title: "Copied", description: "Paste it into an email or a chat." });
    } catch (err) {
      toast({ variant: "destructive", title: "Could not copy", description: errorText(err) });
    }
  };

  const onPdf = async (how: "save" | "share") => {
    if (!body) return;
    try {
      const res = await exportPdf.mutateAsync({ id: meetingId, pick: body, message });
      const file = { url: res.pdfUrl, fileName: res.fileName };
      const done = how === "share" ? await sharePdfNatively(file) : await savePdf(file);
      if (done === "cancelled") return;
      toast({
        variant: "success",
        title: done === "saved" ? "PDF saved" : done === "shared" ? "PDF ready to share" : "PDF ready",
        description: res.fileName,
      });
    } catch (err) {
      toast({ variant: "destructive", title: "Could not make the PDF", description: errorText(err) });
    }
  };

  const sections = (cat?.sections ?? []).filter((s) => s.available);
  const presets = (cat?.presets ?? []).filter((p) => p.available);
  const messageMax = cat?.messageMax ?? 500;

  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      <FormSheetContent side="palette">
        <FormSheetFrame>
          <FormSheetHeader>
            <FormSheetTitle>Export / Send</FormSheetTitle>
            <FormSheetDescription>
              Pick the parts to send. You send it yourself: download the PDF, or copy the text into an
              email or a chat.
            </FormSheetDescription>
          </FormSheetHeader>

          <FormSheetBody className="pb-4">
            {catalogue.isError ? (
              <p role="alert" className="p-5 text-sm text-destructive">
                {errorText(catalogue.error)}
              </p>
            ) : !cat || !pick ? (
              <div className="flex items-center gap-2 p-5 text-sm text-muted-foreground">
                <LoaderCircle className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : (
              <div className="grid min-h-0 grid-cols-1 bg-background md:grid-cols-2">
                <div className="min-w-0 px-4 pb-4 pt-4 md:border-r md:border-border/60 md:px-5">
                  {presets.length ? (
                    <div className="mb-3 flex flex-wrap gap-1.5" role="group" aria-label="Presets">
                      {presets.map((p) => (
                        <button
                          key={p.key}
                          type="button"
                          aria-pressed={activePreset === p.key}
                          onClick={() => setPick(applyPreset(cat, p.key))}
                          className={cn(
                            "h-8 rounded-full px-3.5 text-[13px] font-semibold transition-colors",
                            activePreset === p.key ? "bg-foreground text-background" : CHIP_FILL,
                          )}
                        >
                          {p.label}
                          {p.key === "my_todos" && p.count ? ` (${p.count})` : ""}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <div>
                    {sections.map((s, i) => (
                      <SectionRow
                        key={s.key}
                        cat={cat}
                        section={s}
                        pick={pick}
                        onChange={setPick}
                        first={i === 0}
                        last={i === sections.length - 1}
                      />
                    ))}
                  </div>
                  <label className="mt-4 block">
                    <span className="mb-1 flex items-baseline justify-between text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground/85">
                      Message (optional)
                      <span className="font-mono text-[11px] font-normal normal-case tracking-normal text-muted-foreground">
                        {message.length}/{messageMax}
                      </span>
                    </span>
                    <textarea
                      value={message}
                      maxLength={messageMax}
                      rows={2}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="A short note at the top, e.g. Please do these by Thursday."
                      className="w-full resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </label>
                </div>
                {/* Wide: the preview stays in view while the list scrolls. */}
                <div className="min-w-0 border-t border-border/60 md:sticky md:top-0 md:self-start md:border-t-0">
                  <div className="flex items-center gap-2 border-b border-border/60 px-5 py-2.5 text-xs text-muted-foreground">
                    <FileText className="h-4 w-4 shrink-0 text-brand" />
                    <span className="truncate">{preview.data?.fileName ?? "Preview"}</span>
                    {preview.isFetching ? <LoaderCircle className="ml-auto h-3.5 w-3.5 animate-spin" /> : null}
                  </div>
                  {preview.isError ? (
                    <p role="alert" className="p-5 text-sm text-destructive">
                      {errorText(preview.error)}
                    </p>
                  ) : (
                    <Preview data={body ? preview.data : undefined} loading={preview.isFetching} empty={!body} />
                  )}
                </div>
              </div>
            )}
          </FormSheetBody>

          {/* Wraps on a narrow window (three buttons in the Mac app). */}
          <FormSheetFooter className="flex-wrap">
            <FormSheetFooterCell>
              <Button
                type="button"
                variant="ghost"
                onClick={() => void onCopy()}
                disabled={!body || busy}
                className={cn(formSheetActionClass, CHIP_FILL)}
              >
                {exportText.isPending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
                Copy text
              </Button>
            </FormSheetFooterCell>
            {nativeShare ? (
              <FormSheetFooterCell>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => void onPdf("share")}
                  disabled={!body || busy}
                  className={cn(formSheetActionClass, CHIP_FILL)}
                >
                  <Share2 className="h-4 w-4" />
                  Share PDF…
                </Button>
              </FormSheetFooterCell>
            ) : null}
            <FormSheetFooterCell>
              <Button
                type="button"
                onClick={() => void onPdf("save")}
                disabled={!body || busy}
                className={cn(formSheetActionClass, "bg-primary text-primary-foreground hover:bg-primary/90")}
              >
                {exportPdf.isPending ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowDownToLine className="h-4 w-4" />
                )}
                {nativeShare ? "Save PDF" : "Download PDF"}
              </Button>
            </FormSheetFooterCell>
          </FormSheetFooter>
        </FormSheetFrame>
      </FormSheetContent>
    </FormSheet>
  );
}
